import classNames from 'classnames'
import { usePathname } from 'next/navigation'
import { useTranslation } from 'next-i18next'
import React, { useEffect, useMemo, useState, useRef } from 'react'

import { useQuinceRouter } from '@src/modules/navigation'
import { EFilterKeys } from '@src/pages/[...slug]/components/collection/domains/CollectionFilter'
import CollectionFilterLoader from '@src/pages/[...slug]/components/collection/molecules/collectionFilters/CollectionFilterLoader'
import { CheckboxGroup, ColorFilterGroup, DropdownV2, Icon, RadioButtonGroup } from '@src/quince-components/src/atoms'
import StickyWrapper from '@src/quince-components/src/atoms/stickyWrapper'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'
import { getSessionStorage } from '@src/utils/enhancedSessionStorage'

import { FilterCollapseGroup } from '../../components/FilterCollapseGroup'
import { FilterDrawer } from '../../components/FilterDrawer'
import { FilterDropdown } from '../../components/FilterDropdown'
import { FILTER_DROPDOWN_TYPES, MAX_FILTERS, SORT_OPTIONS_CLP_EXPERIMENT } from '../../constants/constants'
import {
  CLEAR_ALL_THRESHOLD,
  COLORS_FILTER_KEY,
  FALLBACK_COLOR,
  FILTER_TYPES,
  GLOBAL_FILTER_KEY,
} from '../../constants/filterSection.constants'
import { useEnhancedFilterState } from '../../hooks/useEnhancedFilterState'
import { FilterSectionWithDrawerProps, FilterItem, CheckboxOption } from '../filterSection.interface'
import {
  toTitleCase,
  getSelectedCount,
  createCheckboxOptions,
  excludeDepartmentFilter,
  handleUrlBasedProductTypeSelection,
  handleRadioFilterChange,
  transformSubCollectionCards,
  getDisplayNameForFilterValue,
} from '../filterUtils'
import FilterInlineIcon from '../icon/filter.inline.svg'

import styles from './filterSectionWithDrawer.module.scss'

const FILTER_DRAWER_STORAGE_KEY = 'collection-filter-drawer'
const FILTER_COLLAPSE_STATE_KEY = 'collection-filter-collapse-state'

export const FilterSectionWithDrawer: React.FC<FilterSectionWithDrawerProps> = props => {
  const {
    className,
    filters: serverFilters = [],
    noOfItems = 0,
    selectedFilters = {},
    sortBy = '',
    hideFilters = false,
    skipSorting = false,
    collectionSlug,
    updateQuery,
    showShimmer,
    showGroupedPage = false,
    subCollectionCards,
    filtersContainerClassName,
  } = props

  const pathname = usePathname()

  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [collapseActiveKeys, setCollapseActiveKeys] = useState<string[]>([])

  useEffect(() => {
    try {
      const storage = getSessionStorage()
      const storedPath = storage.getItem<string>(FILTER_DRAWER_STORAGE_KEY, { isGlobal: true })
      if (storedPath === pathname) {
        setIsDrawerOpen(true)
        const storedCollapseState = storage.getItem<string>(FILTER_COLLAPSE_STATE_KEY, { isGlobal: true })
        if (storedCollapseState) {
          setCollapseActiveKeys(JSON.parse(storedCollapseState))
        }
      }
    } catch (e) {
      console.error('Failed to read drawer state from session storage', e)
    }
  }, [pathname])

  const typedSubCollectionCards = useMemo(() => transformSubCollectionCards(subCollectionCards), [subCollectionCards])
  const router = useQuinceRouter()
  const routerAsPath = router.asPath.split('?')[0] ?? ''

  const [isSticky, setIsSticky] = useState(false)
  const openDropdownRefs = useRef<Set<string>>(new Set())
  const [hasHandledStickyChange, setHasHandledStickyChange] = useState(false)

  const closeAllDropdowns = () => {
    openDropdownRefs.current.clear()
    setIsSticky(prev => prev)
  }

  const {
    selectedFiltersState,
    selectedFilterChips,
    clearAllFilters,
    handleCheckboxFilterChange,
    handleColorChange,
    handleChipDismissByLabel,
    effectiveTotalSelectedFilters,
  } = useEnhancedFilterState({
    selectedFilters,
    showGroupedPage,
    routerAsPath,
    serverFilters,
    updateQuery,
    subCollectionCards,
  })

  const { t } = useTranslation('common')
  const filters = useMemo(() => {
    const filteredList = excludeDepartmentFilter(serverFilters, collectionSlug)
      .filter(item => item.values.length > 1)
      .slice(0, MAX_FILTERS)
    return filteredList
  }, [serverFilters, collectionSlug])

  const routerContext = useMemo(() => ({ router, routerAsPath }), [router, routerAsPath])
  const commonParams = useMemo(
    () => ({ showGroupedPage, updateQuery, filters, subCollectionCards: typedSubCollectionCards, serverFilters }),
    [showGroupedPage, updateQuery, filters, typedSubCollectionCards, serverFilters],
  )

  useEffect(() => {
    if (isDrawerOpen) {
      try {
        const storage = getSessionStorage()
        storage.setItem(FILTER_COLLAPSE_STATE_KEY, JSON.stringify(collapseActiveKeys), { isGlobal: true })
      } catch (error) {
        console.error('Error storing collapse state:', error)
      }
    }
  }, [collapseActiveKeys, isDrawerOpen])

  if (showShimmer && !isDrawerOpen) {
    return <CollectionFilterLoader containerClassName={filtersContainerClassName} />
  }

  const shouldHideFilters = hideFilters || filters.length === 0

  const handleSortChange = (value: string) => {
    updateQuery({ sort: { type: 'toggle', value } })
  }

  const renderSortDropdown = () => {
    if (skipSorting) return null
    const selectedSortOption = SORT_OPTIONS_CLP_EXPERIMENT.find(opt => opt.value === sortBy)
    return (
      <FilterDropdown
        type={FILTER_DROPDOWN_TYPES.SORT}
        label={
          <span>
            {t('sort_by')}
            {`: ${t(selectedSortOption?.labelKey ?? '')}`}
          </span>
        }
        value={sortBy}
        onChange={handleSortChange}
        placement="bottomRight"
        menuClassName={styles.sortDropdownMenu}
        buttonClassName={styles.sortDropdown}
        isMobile={false}
        isActive={sortBy !== ''}
        onTriggerClick={(_, isOpen) => {
          if (isOpen) {
            trackEvent(TELEMETRY_EVENT_NAMES.SORT_OPENED, {})
          }
        }}
        // eslint-disable-next-line i18next/no-literal-string
        dropdownKey="filter-section-drawer-sort"
        forceClose={isSticky}
      />
    )
  }

  if (shouldHideFilters && skipSorting) {
    return null
  }

  if (shouldHideFilters) {
    return (
      <div className={classNames(styles.filtersContainer, className)}>
        <div className={styles.filtersWrapper}>
          <div className={styles.filtersLeft}></div>
          <div className={styles.filtersRight}>{renderSortDropdown()}</div>
        </div>
      </div>
    )
  }

  const handleMainFilterClick = () => {
    const newCollapseKeys = filters.map(filter => filter.name)
    setCollapseActiveKeys(newCollapseKeys)
    setIsDrawerOpen(true)
    try {
      const storage = getSessionStorage()
      storage.setItem(FILTER_DRAWER_STORAGE_KEY, pathname, { isGlobal: true })
      storage.setItem(FILTER_COLLAPSE_STATE_KEY, JSON.stringify(newCollapseKeys), { isGlobal: true })
    } catch (error) {
      console.error('Error storing filter drawer state in sessionStorage:', error)
    }
    trackEvent(TELEMETRY_EVENT_NAMES.FILTER_OPENED, {
      filter_key: GLOBAL_FILTER_KEY,
    })
  }

  const handleFilterDropdownTriggerClick = (filterKey: string, isOpen: boolean) => {
    if (isOpen) {
      const filterName = filterKey === EFilterKeys.SUBCOLLECTIONS ? EFilterKeys.PRODUCT_TYPE : filterKey
      trackEvent(TELEMETRY_EVENT_NAMES.FILTER_OPENED, {
        filter_key: filterName,
      })
    }
  }

  const closeDrawer = () => {
    setIsDrawerOpen(false)
    try {
      const storage = getSessionStorage()
      storage.removeItem(FILTER_DRAWER_STORAGE_KEY, { isGlobal: true })
      storage.removeItem(FILTER_COLLAPSE_STATE_KEY, { isGlobal: true })
    } catch (error) {
      console.error('Error removing filter drawer state from sessionStorage:', error)
    }
  }

  const handleCollapseChange = (activeKeys: string[]) => {
    const newlyOpenedFilters = activeKeys.filter(key => !collapseActiveKeys.includes(key))
    newlyOpenedFilters.forEach(filterKey => {
      const filterName = filterKey === EFilterKeys.SUBCOLLECTIONS ? EFilterKeys.PRODUCT_TYPE : filterKey
      trackEvent(TELEMETRY_EVENT_NAMES.FILTER_OPENED, {
        filter_key: filterName,
      })
    })
    setCollapseActiveKeys(activeKeys)
  }

  const formatFilterLabel = (
    filterLabel: string,
    selectedCount: number,
    _singleSelectedValue?: string,
  ): React.ReactNode => {
    return (
      <>
        {toTitleCase(filterLabel)}
        {selectedCount > 0 && ` (${selectedCount})`}
      </>
    )
  }

  const renderCheckboxFilter = (
    filter: FilterItem,
    options: CheckboxOption[],
    onChange: (selectedOptions: CheckboxOption[]) => void,
    isDrawer = false,
    forceClose = false,
  ) => {
    const selectedCount = getSelectedCount(filter, selectedFiltersState)
    const selectedOptions = options.filter(option => option.checked)
    if (isDrawer) {
      return (
        <CheckboxGroup
          key={`drawer-${filter.name}-${selectedCount}`}
          options={options}
          onChange={onChange}
          direction="column"
          maxVisible={8}
          className={styles.checkboxGroup}
          showMoreButtonClassName={styles.showMoreButtonDrawer}
        />
      )
    }
    const formatCheckboxFilterLabel = () => {
      let singleSelectedValue: string | undefined
      if (selectedCount === 1 && selectedOptions.length === 1) {
        const selectedValue = selectedOptions[0]?.name
        // Use getDisplayNameForFilterValue for consistent translation
        if (selectedValue) {
          const currentValue = getDisplayNameForFilterValue({
            filters: serverFilters,
            filterName: filter.name,
            filterValue: selectedValue,
          })

          singleSelectedValue = currentValue
        }
      }
      return formatFilterLabel(filter.config.label, selectedCount, singleSelectedValue)
    }
    return (
      <DropdownV2
        key={`${filter.name}-filter`}
        menu={{
          items: [
            {
              key: `${filter.name}-filter`,
              label: (
                <CheckboxGroup
                  key={`checkbox-${selectedCount}`}
                  options={options}
                  onChange={onChange}
                  direction="column"
                  maxVisible={8}
                  className={styles.checkboxPadding}
                />
              ),
              onClick: undefined,
            },
          ],
          onClick: undefined,
        }}
        trigger={['click']}
        placement="bottomLeft"
        autoAdjustOverflow={false}
        isActive={selectedCount > 0}
        multiSelect={true}
        buttonClassName={styles.mobileFilterButton}
        onTriggerClick={(_, isOpen) => {
          handleFilterDropdownTriggerClick(filter.name, isOpen)
        }}
        dropdownKey={`filter-section-drawer-${filter.name}`}
        forceClose={forceClose}
      >
        <span>{formatCheckboxFilterLabel()}</span>
      </DropdownV2>
    )
  }

  const renderColorFilter = (filter: FilterItem, isDrawer = false, forceClose = false) => {
    const selectedCount = getSelectedCount(filter, selectedFiltersState)
    const colorOptions = filter.values.map(value => {
      if (typeof value === 'string') {
        return { value, hex: FALLBACK_COLOR, label: value }
      }
      const colorValue = value
      return {
        value: colorValue.name,
        hex: colorValue.hex ?? FALLBACK_COLOR,
        label: colorValue.displayName ?? colorValue.name,
        imgURL: colorValue.imgURL ?? undefined,
      }
    })
    const handleColorChangeForFilter = (value: string) => {
      handleColorChange(filter, value)
    }
    if (isDrawer) {
      return (
        <ColorFilterGroup
          key={`drawer-color-group-${selectedFiltersState[filter.name]?.length}`}
          options={colorOptions}
          selectedValues={selectedFiltersState[filter.name] ?? []}
          onChange={handleColorChangeForFilter}
          direction="horizontal"
          className={styles.colorFilterGroupDrawer}
          columns={5}
          circleSize={2.2}
          rowGap={1.6}
          columnGap={0}
        />
      )
    }
    const formatColorFilterLabel = () => {
      let singleSelectedValue: string | undefined
      if (selectedCount === 1 && selectedFiltersState[filter.name]?.length === 1) {
        const selectedValue = selectedFiltersState[filter.name]?.[0]
        // Use getDisplayNameForFilterValue for consistent translation
        if (selectedValue) {
          const currentValue = getDisplayNameForFilterValue({
            filters: serverFilters,
            filterName: filter.name,
            filterValue: selectedValue,
          })
          singleSelectedValue = currentValue
        }
      }
      return formatFilterLabel(filter.config.label, selectedCount, singleSelectedValue)
    }
    return (
      <DropdownV2
        key={`${filter.name}-filter`}
        menu={{
          items: [
            {
              key: COLORS_FILTER_KEY,
              label: (
                <ColorFilterGroup
                  key={`color-group-${selectedFiltersState[filter.name]?.length}`}
                  options={colorOptions}
                  selectedValues={selectedFiltersState[filter.name] ?? []}
                  onChange={handleColorChangeForFilter}
                  direction="horizontal"
                  circleClassName={styles.colorFilterCircle}
                  className={styles.colorFilterGroup}
                  columns={3}
                  circleSize={3.6}
                  rowGap={2}
                  columnGap="auto"
                />
              ),
            },
          ],
        }}
        trigger={['click']}
        placement="bottomLeft"
        autoAdjustOverflow={false}
        isActive={selectedCount > 0}
        multiSelect={true}
        buttonClassName={styles.mobileFilterButton}
        onTriggerClick={(_, isOpen) => {
          handleFilterDropdownTriggerClick(filter.name, isOpen)
        }}
        dropdownKey={`filter-section-drawer-${filter.name}`}
        forceClose={forceClose}
      >
        <span>{formatColorFilterLabel()}</span>
      </DropdownV2>
    )
  }

  const renderRadioFilter = (
    filter: FilterItem,
    options: CheckboxOption[],
    _onChange: (selectedOptions: CheckboxOption[]) => void,
    isDrawer = false,
    forceClose = false,
  ) => {
    const filterName = filter.name === EFilterKeys.PRODUCT_TYPE ? EFilterKeys.SUBCOLLECTIONS : filter.name
    const mappedFilter = filterName !== filter.name ? { ...filter, name: filterName } : filter
    let currentValue = selectedFiltersState[filterName]?.[0] ?? ''
    let hasUrlBasedSelection = false
    if (filter.name === EFilterKeys.PRODUCT_TYPE && showGroupedPage) {
      const { currentValue: urlBasedValue, hasUrlMatch } = handleUrlBasedProductTypeSelection(
        filter,
        options,
        routerAsPath,
        selectedFiltersState,
        typedSubCollectionCards,
      )
      currentValue = urlBasedValue
      hasUrlBasedSelection = hasUrlMatch
    }
    const selectedCount = hasUrlBasedSelection ? 1 : getSelectedCount(mappedFilter, selectedFiltersState)
    const selectedOptions = options.filter(option => option.checked)
    const handleRadioChange = (value: string): void => {
      handleRadioFilterChange(routerContext, { ...commonParams, value, options, filter })
    }
    if (isDrawer) {
      return (
        <RadioButtonGroup
          key={`drawer-${filterName}-${selectedCount}`}
          options={options.map(opt => ({
            label: opt.label,
            value: opt.id,
            checked: opt.checked,
          }))}
          onChange={handleRadioChange}
          name={filterName}
          className={styles.radioGroup}
          value={options.find(opt => opt.name === currentValue || opt.label === currentValue)?.id}
          itemsClassName={styles.radioItems}
          radioItemClassName={styles.drawerRadioItem}
          radioLabelClassName={styles.radioLabel}
        />
      )
    }
    const formatRadioFilterLabel = () => {
      let singleSelectedValue: string | undefined
      if (selectedCount === 1 && selectedOptions.length === 1) {
        const selectedValue = selectedOptions[0]?.name
        // Use getDisplayNameForFilterValue for consistent translation
        if (selectedValue) {
          singleSelectedValue = getDisplayNameForFilterValue({
            filters: serverFilters,
            filterName: filter.name,
            filterValue: selectedValue,
          })
        }
      }
      return formatFilterLabel(filter.config.label, selectedCount, singleSelectedValue)
    }
    return (
      <DropdownV2
        key={filter.name}
        menu={{
          items: [
            {
              key: `${filter.name}-filter`,
              label: (
                <RadioButtonGroup
                  key={`radio-${selectedCount}`}
                  options={options.map(opt => ({
                    label: opt.label,
                    value: opt.id,
                    checked: opt.checked,
                  }))}
                  onChange={handleRadioChange}
                  name={filter.name}
                  className={styles.radioPadding}
                  value={options.find(opt => opt.name === currentValue || opt.label === currentValue)?.id}
                  itemsClassName={styles.radioItems}
                  radioItemClassName={styles.radioItem}
                  radioLabelClassName={styles.radioLabel}
                />
              ),
              onClick: undefined,
            },
          ],
          onClick: undefined,
        }}
        trigger={['click']}
        placement="bottomLeft"
        autoAdjustOverflow={false}
        isActive={selectedCount > 0}
        buttonClassName={styles.mobileFilterButton}
        onTriggerClick={(_, isOpen) => {
          handleFilterDropdownTriggerClick(filter.name, isOpen)
        }}
        dropdownKey={`filter-section-drawer-${filter.name}`}
        forceClose={forceClose}
      >
        <span>{formatRadioFilterLabel()}</span>
      </DropdownV2>
    )
  }

  const renderFilterComponent = (filter: FilterItem, isDrawer = false, forceClose = false) => {
    const { config, name: filterName } = filter
    const filterType = config.type.toLowerCase()
    const state = selectedFiltersState[filterName] ?? []
    switch (filterType) {
      case FILTER_TYPES.CARD:
        return renderRadioFilter(
          filter,
          createCheckboxOptions(filter.values, state, filter.name),
          selectedOptions => {
            handleCheckboxFilterChange(filter, selectedOptions)
          },
          isDrawer,
          forceClose,
        )
      case FILTER_TYPES.CHECKBOX:
        if (filterName === EFilterKeys.COLORS) {
          return renderColorFilter(filter, isDrawer, forceClose)
        }
        return renderCheckboxFilter(
          filter,
          createCheckboxOptions(filter.values, state, filter.name),
          selectedOptions => {
            handleCheckboxFilterChange(filter, selectedOptions)
          },
          isDrawer,
          forceClose,
        )
      default:
        return null
    }
  }

  const renderFilter = (isIntersecting: boolean) => {
    if (isSticky !== !isIntersecting) {
      setIsSticky(!isIntersecting)
      if (!hasHandledStickyChange) {
        closeAllDropdowns()
        setHasHandledStickyChange(true)
      }
    }
    if (isIntersecting && hasHandledStickyChange) {
      setHasHandledStickyChange(false)
    }
    return (
      <div
        data-testid="filters-container"
        className={classNames(styles.filtersContainer, filtersContainerClassName, className, {
          [`${styles.filtersContainerSticky}`]: !isIntersecting,
        })}
      >
        <div className={styles.filtersWrapper}>
          <div className={styles.filtersLeft}>
            <button
              className={classNames(
                styles.filterButton,
                effectiveTotalSelectedFilters > 0 && styles.filterButtonActive,
              )}
              onClick={handleMainFilterClick}
              data-testid={`filter-drawer-button-${effectiveTotalSelectedFilters}`}
            >
              <Icon iconSvg={FilterInlineIcon} className={styles.filterIcon} />
              {t('filter')} {effectiveTotalSelectedFilters > 0 && `(${effectiveTotalSelectedFilters})`}
            </button>
            {filters.map(filter => (
              <div key={filter.name} className={styles.filterItem}>
                {renderFilterComponent(filter, false, !isIntersecting && !hasHandledStickyChange)}
              </div>
            ))}
            {effectiveTotalSelectedFilters >= CLEAR_ALL_THRESHOLD && (
              <button className={styles.clearAllButton} onClick={clearAllFilters}>
                {t('clear_all_v1')}
              </button>
            )}
          </div>
          <div className={styles.filtersRight}>{renderSortDropdown()}</div>
          <FilterDrawer
            placement="left"
            open={isDrawerOpen}
            onClose={closeDrawer}
            selectedFilterChips={selectedFilterChips}
            onChipDismiss={handleChipDismissByLabel}
            clearAllFilters={clearAllFilters}
            totalSelectedFilters={effectiveTotalSelectedFilters}
            noOfItems={noOfItems}
            headerClassName={`${styles.drawerCustomHeader} filterDrawerExperimentHeader`}
            bodyClassName={`${styles.drawerBody} ${styles.noPaddingTop}`}
            isMobile={false}
          >
            <FilterCollapseGroup
              filters={filters}
              selectedFiltersState={selectedFiltersState}
              activeItems={collapseActiveKeys}
              onChange={handleCollapseChange}
              renderFilterComponent={renderFilterComponent}
              isMobile={false}
            />
          </FilterDrawer>
        </div>
      </div>
    )
  }

  return (
    <div id="stickyWrapperFilterSectionWithDrawer" data-testid="stickyWrapperFilterSectionWithDrawer">
      <StickyWrapper renderChildren={renderFilter} stickyWrapperClassName={isDrawerOpen ? styles.stickyWrapper : ''} />
    </div>
  )
}
